import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { RefreshCw } from "lucide-react";

const MONTHS_LBL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const YEARS = Array.from({ length: 11 }, (_, i) => 2020 + i);

/**
 * Generic monthly-readings table.
 *  - collection: target Mongo collection (e.g. "energy_readings")
 *  - filters: extra filters when listing (e.g. {fuel: "diesel"})
 *  - numericKeys: array of {key, label, factor?, derived?} — each is one set of monthly
 *    values stored on the same yearly document under `${key}_jan...dec`.
 *  - headerCards: function(yearRow, allRows) → array of {label, value, sub?}
 *  - chart: optional config {type:"bar"|"grouped", title, series:[{key,label,color}]}
 *  Inline editable: click any numeric cell → input → blur/Enter to commit (only commits to local state; "Guardar" persists to backend).
 */
export default function MonthlyReadings({
  collection,
  title,
  numericKeys,
  filters = {},
  headerCards,
  chart,
  extraColumns = [], // [{key, label, type}] — non-numeric columns on the yearly row (e.g. cost)
}) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);    // all yearly rows for this collection
  const [current, setCurrent] = useState(null);   // the row for the selected year (or new)
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // {key, month}
  const [recalcTick, setRecalcTick] = useState(0);

  async function load() {
    const params = { ...filters };
    const { data } = await api.get(`/data/${collection}`, { params });
    setRows(data);
    const yr = data.find((r) => r.year === year);
    if (yr) {
      setCurrent({ ...yr });
    } else {
      // New blank row for this year
      const blank = { year, ...filters };
      numericKeys.forEach((nk) => MONTH_KEYS.forEach((m) => { blank[`${nk.key}_${m}`] = 0; }));
      extraColumns.forEach((c) => { blank[c.key] = c.default ?? ""; });
      setCurrent(blank);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection, year, JSON.stringify(filters), recalcTick]);

  function setCellValue(nk, mk, val) {
    const n = val === "" ? "" : Number(val);
    setCurrent((c) => ({ ...c, [`${nk}_${mk}`]: Number.isNaN(n) ? 0 : n }));
  }

  async function save() {
    if (!current) return;
    setSaving(true);
    const payload = { ...current };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at;
    delete payload._id;
    try {
      if (current.id) {
        await api.patch(`/data/${collection}/${current.id}`, payload);
      } else {
        const { data } = await api.post(`/data/${collection}`, payload);
        setCurrent(data);
      }
      load();
    } finally { setSaving(false); }
  }

  // Compute YTDs reactively
  const ytdByKey = useMemo(() => {
    if (!current) return {};
    const out = {};
    numericKeys.forEach((nk) => {
      out[nk.key] = MONTH_KEYS.reduce((a, mk) => a + (Number(current[`${nk.key}_${mk}`]) || 0), 0) * (nk.factor || 1);
    });
    return out;
  }, [current, numericKeys]);

  const cards = headerCards ? headerCards(current || {}, ytdByKey, rows) : [];

  // Chart data
  const chartData = useMemo(() => {
    if (!current || !chart) return [];
    return MONTHS_LBL.map((m, i) => {
      const row = { month: m };
      chart.series.forEach((s) => {
        const factor = numericKeys.find((nk) => nk.key === s.key)?.factor || 1;
        row[s.key] = (Number(current[`${s.key}_${MONTH_KEYS[i]}`]) || 0) * factor;
      });
      return row;
    });
  }, [current, chart, numericKeys]);

  return (
    <div data-testid={`monthly-${collection}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          {title && <div className="card-title">{title}</div>}
        </div>
        <div className="flex gap-2 items-center">
          <label className="label" style={{ margin: 0 }}>Año:</label>
          <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 110 }} data-testid={`${collection}-year-select`}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-sm" onClick={() => setRecalcTick(recalcTick + 1)} title="Recargar y recalcular YTD" data-testid={`${collection}-recalc`}>
            <RefreshCw size={12} /> Recalcular YTD
          </button>
          <button className="btn btn-sm btn-primary" onClick={save} disabled={saving} data-testid={`${collection}-save`}>
            {saving ? <span className="spinner" /> : "Guardar"}
          </button>
        </div>
      </div>

      {cards.length > 0 && (
        <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `repeat(${Math.min(cards.length, 4)}, 1fr)` }}>
          {cards.map((c, i) => (
            <div key={i} className="kpi-card" data-testid={`${collection}-card-${i}`}>
              <div className="label">{c.label}</div>
              <div className="value" style={{ fontSize: 22 }}>{c.value}</div>
              {c.sub && <div className="sub">{c.sub}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="table-wrap" style={{ overflowX: "auto", maxWidth: "100%" }}>
        <table className="table" style={{ minWidth: 1300 }}>
          <thead>
            <tr>
              <th>Serie</th>
              {MONTHS_LBL.map((m) => <th key={m} style={{ minWidth: 80 }}>{m}</th>)}
              <th>YTD</th>
              {extraColumns.map((c) => <th key={c.key}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {numericKeys.filter((nk) => !nk.derived).map((nk) => (
              <tr key={nk.key}>
                <td><b>{nk.label}</b></td>
                {MONTH_KEYS.map((mk) => {
                  const v = current ? (current[`${nk.key}_${mk}`] ?? 0) : 0;
                  const isEditing = editingCell?.key === nk.key && editingCell?.month === mk;
                  return (
                    <td key={mk}
                      onClick={() => setEditingCell({ key: nk.key, month: mk })}
                      style={{ cursor: "text", padding: 4 }}
                      data-testid={`${collection}-${nk.key}-${mk}`}
                    >
                      {isEditing ? (
                        <input
                          autoFocus
                          type="number"
                          step="any"
                          className="input"
                          style={{ padding: 4, fontSize: 12, width: "100%" }}
                          defaultValue={v}
                          onBlur={(e) => { setCellValue(nk.key, mk, e.target.value); setEditingCell(null); }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { setCellValue(nk.key, mk, e.target.value); setEditingCell(null); }
                            if (e.key === "Escape") setEditingCell(null);
                          }}
                        />
                      ) : (
                        <span className="text-xs" style={{ display: "block", textAlign: "right" }}>
                          {Number(v).toLocaleString("es-MX", { maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </td>
                  );
                })}
                <td><b>{(ytdByKey[nk.key] || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}</b></td>
                {extraColumns.map((c) => <td key={c.key}></td>)}
              </tr>
            ))}
            {extraColumns.length > 0 && (
              <tr>
                <td colSpan={13}></td>
                <td></td>
                {extraColumns.map((c) => (
                  <td key={c.key}>
                    <input type={c.type === "number" ? "number" : "text"} className="input" style={{ padding: 4, fontSize: 12 }}
                      value={current?.[c.key] ?? ""}
                      onChange={(e) => setCurrent({ ...current, [c.key]: c.type === "number" ? Number(e.target.value) : e.target.value })}
                      data-testid={`${collection}-extra-${c.key}`}
                    />
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {chart && (
        <div className="mt-4">
          <div className="text-sm font-semibold mb-2">{chart.title}</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
              <YAxis stroke="var(--text-dim)" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
              <Legend />
              {chart.series.map((s) => (
                <Bar key={s.key} dataKey={s.key} fill={s.color} name={s.label} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export { MONTHS_LBL, MONTH_KEYS };
